import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

// 1. Fetch Reminders
export const fetchReminders = createAsyncThunk(
  "reminder/fetchReminders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl}/Reminder/list`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch reminders"
      );
    }
  }
);

// 2. Fetch Reminder Stats
export const fetchReminderStats = createAsyncThunk(
  "reminder/fetchReminderStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl}/Reminder/stats`, { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch reminder stats"
      );
    }
  }
);

// 3. Create Reminder
export const createReminder = createAsyncThunk(
  "reminder/createReminder",
  async (reminderData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BaseUrl}/Reminder/create`, reminderData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create reminder"
      );
    }
  }
);

// 4. Update Reminder
export const updateReminder = createAsyncThunk(
  "reminder/updateReminder",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BaseUrl}/Reminder/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update reminder"
      );
    }
  }
);

// 5. Complete Reminder
export const completeReminder = createAsyncThunk(
  "reminder/completeReminder",
  async ({ id, userId, notes }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BaseUrl}/Reminder/${id}/complete`, {
        userId,
        notes,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to complete reminder"
      );
    }
  }
);

// 6. Snooze Reminder
export const snoozeReminder = createAsyncThunk(
  "reminder/snoozeReminder",
  async ({ id, minutes, customDate, customTime, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BaseUrl}/Reminder/${id}/snooze`, {
        minutes,
        customDate,
        customTime,
        userId,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to snooze reminder"
      );
    }
  }
);

// 7. Delete Reminder
export const deleteReminder = createAsyncThunk(
  "reminder/deleteReminder",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BaseUrl}/Reminder/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete reminder"
      );
    }
  }
);

// 8. Dismiss Reminder
export const dismissReminder = createAsyncThunk(
  "reminder/dismissReminder",
  async ({ id, userId }, { rejectWithValue }) => {
    try {
      await axios.put(`${BaseUrl}/Reminder/${id}/dismiss`, { userId });
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to dismiss reminder"
      );
    }
  }
);

// 9. Dismiss All Reminders
export const dismissAllReminders = createAsyncThunk(
  "reminder/dismissAllReminders",
  async ({ reminderIds, userId }, { rejectWithValue }) => {
    try {
      await axios.put(`${BaseUrl}/Reminder/dismiss-all`, { reminderIds, userId });
      return reminderIds;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to dismiss all reminders"
      );
    }
  }
);

// 10. Fetch Due Reminder Alerts (missed / due while offline or logged out)
export const fetchDueReminderAlerts = createAsyncThunk(
  "reminder/fetchDueReminderAlerts",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl}/Reminder/due-alerts`, {
        params: { userId },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch due alerts"
      );
    }
  }
);

// 11. Mark Reminder Alert Shown in Account
export const markReminderShown = createAsyncThunk(
  "reminder/markReminderShown",
  async ({ id, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BaseUrl}/Reminder/${id}/mark-shown`, { userId });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark reminder shown"
      );
    }
  }
);

const initialState = {
  reminders: [],
  pagination: { page: 1, limit: 50, total: 0, totalPages: 1 },
  stats: {
    total: 0,
    upcoming: 0,
    active: 0,
    snoozed: 0,
    overdue: 0,
    completed: 0,
  },
  activeAlerts: [], // Array of triggered reminders showing in the alert popup/modal
  loading: false,
  error: null,
};

const reminderSlice = createSlice({
  name: "reminder",
  initialState,
  reducers: {
    // Real-time alert added from Socket.io 'reminder_alert'
    triggerReminderAlert: (state, action) => {
      const alert = action.payload;
      if (!alert || !alert._id) return;
      const exists = state.activeAlerts.some((a) => a._id === alert._id);
      if (!exists) {
        state.activeAlerts.push(alert);
      }
      // Also update in list if present
      const index = state.reminders.findIndex((r) => r._id === alert._id);
      if (index !== -1) {
        state.reminders[index] = { ...state.reminders[index], ...alert, status: "Triggered" };
      }
      state.stats.active += 1;
    },
    // Dismiss single alert from activeAlerts modal
    dismissAlertModal: (state, action) => {
      const alertId = action.payload;
      state.activeAlerts = state.activeAlerts.filter((a) => a._id !== alertId);
    },
    // Clear all active alerts
    clearAllAlerts: (state) => {
      state.activeAlerts = [];
    },
    // Update single reminder status in state
    updateReminderInState: (state, action) => {
      const updated = action.payload;
      if (!updated || !updated._id) return;
      const index = state.reminders.findIndex((r) => r._id === updated._id);
      if (index !== -1) {
        state.reminders[index] = { ...state.reminders[index], ...updated };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchReminders
      .addCase(fetchReminders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReminders.fulfilled, (state, action) => {
        state.loading = false;
        state.reminders = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchReminders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchReminderStats
      .addCase(fetchReminderStats.fulfilled, (state, action) => {
        state.stats = action.payload || state.stats;
      })

      // createReminder
      .addCase(createReminder.fulfilled, (state, action) => {
        state.reminders.unshift(action.payload);
        state.stats.total += 1;
        state.stats.upcoming += 1;
      })

      // updateReminder
      .addCase(updateReminder.fulfilled, (state, action) => {
        const index = state.reminders.findIndex((r) => r._id === action.payload._id);
        if (index !== -1) {
          state.reminders[index] = action.payload;
        }
      })

      // completeReminder
      .addCase(completeReminder.fulfilled, (state, action) => {
        const completedItem = action.payload;
        const index = state.reminders.findIndex((r) => r._id === completedItem._id);
        if (index !== -1) {
          state.reminders[index] = completedItem;
        }
        // Remove from active alerts if present
        state.activeAlerts = state.activeAlerts.filter((a) => a._id !== completedItem._id);
        state.stats.completed += 1;
        if (state.stats.active > 0) state.stats.active -= 1;
      })

      // snoozeReminder
      .addCase(snoozeReminder.fulfilled, (state, action) => {
        const snoozedItem = action.payload;
        const index = state.reminders.findIndex((r) => r._id === snoozedItem._id);
        if (index !== -1) {
          state.reminders[index] = snoozedItem;
        }
        // Remove from active alerts modal
        state.activeAlerts = state.activeAlerts.filter((a) => a._id !== snoozedItem._id);
        state.stats.snoozed += 1;
        if (state.stats.active > 0) state.stats.active -= 1;
      })

      // deleteReminder
      .addCase(deleteReminder.fulfilled, (state, action) => {
        const id = action.payload;
        state.reminders = state.reminders.filter((r) => r._id !== id);
        state.activeAlerts = state.activeAlerts.filter((a) => a._id !== id);
        if (state.stats.total > 0) state.stats.total -= 1;
      })

      // dismissReminder
      .addCase(dismissReminder.fulfilled, (state, action) => {
        const id = action.payload;
        state.activeAlerts = state.activeAlerts.filter((a) => a._id !== id);
      })

      // dismissAllReminders
      .addCase(dismissAllReminders.fulfilled, (state) => {
        state.activeAlerts = [];
      })

      // fetchDueReminderAlerts (missed / due while offline or logged out)
      .addCase(fetchDueReminderAlerts.fulfilled, (state, action) => {
        const incomingAlerts = action.payload || [];
        incomingAlerts.forEach((alert) => {
          if (alert && alert._id) {
            const exists = state.activeAlerts.some((a) => a._id === alert._id);
            if (!exists) {
              state.activeAlerts.push(alert);
            }
          }
        });
      });
  },
});

export const {
  triggerReminderAlert,
  dismissAlertModal,
  clearAllAlerts,
  updateReminderInState,
} = reminderSlice.actions;

export default reminderSlice.reducer;
